import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Google Analytics 4 Measurement ID
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Initialize Google Analytics
export const initGA = () => {
  if (!GA_MEASUREMENT_ID) {
    console.log('[GA] No measurement ID configured');
    return;
  }

  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    page_path: window.location.pathname,
    send_page_view: false // We'll track manually for SPA
  });

  console.log('[GA] Initialized with ID:', GA_MEASUREMENT_ID);
};

// Track page views
export const trackPageView = (path, title) => {
  if (!window.gtag || !GA_MEASUREMENT_ID) return;
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title || document.title
  });
};

// Track custom events
export const trackEvent = (action, category, label, value) => {
  if (!window.gtag) return;
  
  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value
  });
};

// Track conversions
export const trackConversion = (conversionType, value, currency = 'USD') => {
  if (!window.gtag) return;

  const conversionEvents = {
    signup: 'sign_up',
    donation: 'purchase',
    subscription: 'subscribe',
    pledge: 'generate_lead',
    business_registration: 'begin_checkout'
  };

  const eventName = conversionEvents[conversionType] || conversionType;

  window.gtag('event', eventName, {
    currency: currency,
    value: value,
    items: [{
      item_name: conversionType,
      price: value
    }]
  });
};

// Track user properties
export const setUserProperties = (userId, properties = {}) => {
  if (!window.gtag) return;

  window.gtag('set', 'user_properties', {
    user_id: userId,
    ...properties
  });
};

// Custom hook for tracking page views in React Router
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
};

// Pre-defined event trackers
export const GAEvents = {
  // User events
  login: () => trackEvent('login', 'engagement', 'user_login'),
  signup: (method = 'email') => trackEvent('sign_up', 'engagement', method),
  logout: () => trackEvent('logout', 'engagement', 'user_logout'),
  
  // Donation events
  donationStart: (amount) => trackEvent('begin_checkout', 'ecommerce', 'donation', amount),
  donationComplete: (amount) => trackConversion('donation', amount),
  recurringDonationStart: (amount) => trackEvent('subscribe', 'ecommerce', 'recurring_donation', amount),
  
  // Shop events
  viewProduct: (productName) => trackEvent('view_item', 'ecommerce', productName),
  addToCart: (productName, price) => trackEvent('add_to_cart', 'ecommerce', productName, price),
  purchase: (amount) => trackConversion('purchase', amount),
  
  // Engagement events
  pledgeSigned: () => trackConversion('pledge'),
  storySubmitted: () => trackEvent('submit', 'content', 'success_story'),
  businessRegistered: () => trackConversion('business_registration'),
  eventRSVP: (eventName) => trackEvent('rsvp', 'engagement', eventName),
  referralShared: () => trackEvent('share', 'social', 'referral_link'),
  
  // Navigation events
  ctaClick: (ctaName) => trackEvent('cta_click', 'navigation', ctaName),
  menuClick: (menuItem) => trackEvent('menu_click', 'navigation', menuItem),
  externalLink: (url) => trackEvent('click', 'outbound', url),
  
  // Content engagement
  videoPlay: (videoName) => trackEvent('video_start', 'engagement', videoName),
  downloadResource: (resourceName) => trackEvent('download', 'engagement', resourceName),
  searchQuery: (query) => trackEvent('search', 'engagement', query)
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackConversion,
  setUserProperties,
  usePageTracking,
  GAEvents
};
